import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiLoader, FiSend } from "react-icons/fi";
import { getMentionCandidates } from "../../api/announcements";
import { mentionAtCaret } from "../../utils/mentions";
import { getProfileAvatarUrl, getProfileInitial } from "../../utils/userDisplay";

const MAX_HEIGHT = 160;
const LIST_WIDTH = 288;
const LIST_MAX_HEIGHT = 256;

/**
 * The comment box: a textarea that suggests people after "@", and the send button.
 * Enter sends and Shift+Enter starts a new line — except while suggestions are open,
 * when Enter or Tab picks the highlighted person.
 */
const MentionComposer = ({
  postId,
  value,
  onChange,
  onSubmit,
  submitting = false,
  placeholder,
  sendLabel,
  isDark,
  inputRef,
}) => {
  const localRef = useRef(null);
  const ref = inputRef || localRef;
  const [mention, setMention] = useState(null); // { query, start } while typing an @name
  const [people, setPeople] = useState([]);
  const [active, setActive] = useState(0);
  const cache = useRef(new Map());
  const mentionQuery = mention ? mention.query.toLowerCase() : null;

  useEffect(() => {
    if (mentionQuery === null) {
      setPeople([]);
      return undefined;
    }
    if (cache.current.has(mentionQuery)) {
      setPeople(cache.current.get(mentionQuery));
      setActive(0);
      return undefined;
    }
    let alive = true;
    const timer = setTimeout(async () => {
      try {
        const res = await getMentionCandidates(postId, mentionQuery);
        const list = res?.data || [];
        cache.current.set(mentionQuery, list);
        if (alive) {
          setPeople(list);
          setActive(0);
        }
      } catch {
        if (alive) setPeople([]);
      }
    }, 150);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [mentionQuery, postId]);

  // Grow with the text, up to a few lines.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
  }, [value, ref]);

  const syncMention = (el) => setMention(mentionAtCaret(el.value, el.selectionStart ?? el.value.length));

  const pick = (person) => {
    const el = ref.current;
    if (!mention || !person) return;
    const caret = el?.selectionStart ?? value.length;
    const insert = `@${person.username} `;
    const next = value.slice(0, mention.start) + insert + value.slice(caret);
    const position = mention.start + insert.length;
    onChange(next);
    setMention(null);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(position, position);
    });
  };

  const open = !!mention && people.length > 0;

  // The list floats over the page rather than inside the comment box, whose
  // containers clip anything that overflows them. Above the box when there's room.
  const [anchor, setAnchor] = useState(null);
  useLayoutEffect(() => {
    if (!open) {
      setAnchor(null);
      return undefined;
    }
    const place = () => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(LIST_WIDTH, window.innerWidth - 16);
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      const roomAbove = rect.top - 16;
      const roomBelow = window.innerHeight - rect.bottom - 16;
      setAnchor(
        roomAbove >= Math.min(LIST_MAX_HEIGHT, roomBelow)
          ? { left, width, bottom: window.innerHeight - rect.top + 8, maxHeight: Math.min(LIST_MAX_HEIGHT, roomAbove) }
          : { left, width, top: rect.bottom + 8, maxHeight: Math.min(LIST_MAX_HEIGHT, roomBelow) }
      );
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, ref]);

  const onKeyDown = (e) => {
    if (e.nativeEvent?.isComposing) return;
    if (open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const step = e.key === "ArrowDown" ? 1 : -1;
        setActive((i) => (i + step + people.length) % people.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pick(people[active]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMention(null);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const muted = "text-label-secondary";
  const inputClass = "border-transparent bg-fill-tertiary text-label-primary placeholder:text-label-tertiary focus:ring-2 focus:ring-focus-ring";

  return (
    <div className="relative flex items-end gap-2">
      {open && anchor && typeof document !== "undefined" && createPortal(
        <ul
          role="listbox"
          style={{ position: "fixed", ...anchor }}
          className={`z-[200] overflow-y-auto rounded-radius-sm border p-1 shadow-xl ${
            "border-separator-default bg-bg-raised"
          }`}
        >
          {people.map((person, i) => {
            const avatar = getProfileAvatarUrl(person);
            return (
              <li key={person._id} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  // mousedown, not click: the textarea must keep focus and the caret.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(person);
                  }}
                  onMouseEnter={() => setActive(i)}
                  className={`flex w-full items-center gap-2.5 rounded-radius-xs px-2.5 py-2 text-left transition ${
                    i === active ? ("bg-fill-tertiary") : ""
                  }`}
                >
                  {avatar ? (
                    <img src={avatar} alt="" className="size-7 shrink-0 rounded-full object-cover" />
                  ) : (
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-tinted text-footnote-semibold text-accent-text">
                      {getProfileInitial(person, "?")}
                    </span>
                  )}
                  <span className="min-w-0">
                    <span className={`block truncate text-subheadline-semibold text-label-primary`}>
                      @{person.username}
                    </span>
                    {person.businessName && (
                      <span className={`block truncate text-footnote ${muted}`}>{person.businessName}</span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>,
        document.body
      )}
      <textarea
        ref={ref}
        value={value}
        rows={1}
        maxLength={500}
        onChange={(e) => {
          onChange(e.target.value);
          syncMention(e.target);
        }}
        onKeyDown={onKeyDown}
        onClick={(e) => syncMention(e.target)}
        onKeyUp={(e) => {
          if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) syncMention(e.target);
        }}
        onBlur={() => setMention(null)}
        placeholder={placeholder}
        aria-autocomplete="list"
        aria-expanded={open}
        className={`min-h-[40px] flex-1 resize-none rounded-radius-md border px-4 py-2 text-subheadline leading-snug outline-none transition ${inputClass}`}
      />
      <button
        type="button"
        onClick={onSubmit}
        disabled={submitting || !value.trim()}
        className="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent-default text-accent-on transition hover:bg-mint/90 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={sendLabel}
      >
        {submitting ? <FiLoader className="size-4 animate-spin" /> : <FiSend className="size-4" />}
      </button>
    </div>
  );
};

export default MentionComposer;
